import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Users, TrendingUp, HardHat, Sparkles, ShieldCheck } from 'lucide-react';

// --- 1. SETUP DATA ---
export const servicesData = [
  {
    id: 1,
    title: "Preconstruction",
    category: "PLANNING",
    description: "Planning, estimates, and constructability reviews that set every build up for success.",
    icon: <Building2 className="w-10 h-10 text-white" />,
    // Construction Blueprint/Planning Image
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2071&auto=format&fit=crop",
    colorOverlay: "from-orange-900/90 via-orange-800/80 to-transparent",
  },
  {
    id: 2,
    title: "Construction Management",
    category: "EXECUTION",
    description: "Hands-on coordination with trade partners, safety leadership, and schedule certainty.",
    icon: <Users className="w-10 h-10 text-white" />,
    // Site Manager/Workers Image
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070&auto=format&fit=crop",
    colorOverlay: "from-emerald-900/90 via-emerald-800/80 to-transparent",
  },
  {
    id: 3,
    title: "Project Management",
    category: "STEWARDSHIP",
    description: "From groundbreaking to turnover, we steward scope, risk, quality, and budget together.",
    icon: <TrendingUp className="w-10 h-10 text-white" />,
    // Meeting/Strategy Image
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070&auto=format&fit=crop",
    colorOverlay: "from-blue-900/90 via-blue-800/80 to-transparent",
  },
  {
    id: 4,
    title: "Lean Construction",
    category: "EFFICIENCY",
    description: "Collaborative delivery, takt planning, and continuous improvement on every site.",
    icon: <HardHat className="w-10 h-10 text-white" />,
    // Safety/Hardhat Image
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=2070&auto=format&fit=crop",
    colorOverlay: "from-indigo-900/90 via-indigo-800/80 to-transparent",
  },
  {
    id: 5,
    title: "Virtual Design & Construction",
    category: "DIGITAL TWINS",
    description: "Model-first coordination, digital twins, and fabrication-ready deliverables.",
    icon: <Sparkles className="w-10 h-10 text-white" />,
    // Modern Architecture/Design Image
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop",
    colorOverlay: "from-rose-900/90 via-rose-800/80 to-transparent",
  },
  {
    id: 6,
    title: "Risk & Supply Chain",
    category: "LOGISTICS",
    description: "Surety, logistics, and resilient sourcing keep projects moving with confidence.",
    icon: <ShieldCheck className="w-10 h-10 text-white" />,
    // Logistics/Cargo Image
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070&auto=format&fit=crop",
    colorOverlay: "from-slate-900/90 via-slate-800/80 to-transparent",
  },
];

const Services = () => {
  const [activeId, setActiveId] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  const handleCardClick = (id) => {
    setActiveId(id);
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveId((currentId) => {
        const currentIndex = servicesData.findIndex((item) => item.id === currentId);
        const nextIndex = (currentIndex + 1) % servicesData.length;
        return servicesData[nextIndex].id;
      });
    }, 2000); 
    return () => clearInterval(interval);
  }, [isPaused]); 

  return (
    <section 
      className="w-full min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 md:p-8 font-sans"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}
    >
      <div className="w-full max-w-[1400px] mb-8 text-black">
        <motion.h2 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl text-left font-semibold text-black"
            style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}
            >
            Our Services
        </motion.h2>
            <motion.div 
            initial={{ width: 0 }}
            whileInView={{ width: 100 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-1 bg-[#2a3890] rounded-full mb-4 mt-2 sm:mb-5 md:mb-6"
            />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Oswald:wght@400;500&display=swap');
        .font-inter { font-family: "Monstserrat, proxima-nova, Helveti, sans-serif"; }
        .font-oswald { font-family: "Monstserrat, proxima-nova, Helveti, sans-serif"; }

        .delay-300 { animation-delay: 300ms; }
        .delay-500 { animation-delay: 500ms; }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.8s ease-out forwards;
          opacity: 0; 
        }
        .animate-slide-up {
          animation: slideUp 0.8s ease-out forwards;
          opacity: 0; 
        }
      `}</style>

      {/* Main Container */}
      <div className="flex flex-col md:flex-row w-full max-w-[1400px] h-[75vh] gap-4">
        
        {servicesData.map((service) => {
          const isActive = activeId === service.id;
          
          return (
            <div
              key={service.id}
              onClick={() => handleCardClick(service.id)}
              className={`
                relative cursor-pointer bg-cover bg-center bg-no-repeat rounded-lg overflow-hidden
                /* Smooth expansion */
                transition-[flex,opacity] duration-1000 ease-in-out
                ${isActive ? 'flex-[10]' : 'flex-[1] hover:flex-[1.5]'}
                h-[15vh] md:h-auto w-full md:w-auto
                group
              `}
              style={{ 
                backgroundImage: `url(${service.image})`
              }}
            >
              {/* Colored Overlay */}
              <div className={`
                absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r 
                transition-all duration-1000 ease-in-out
                ${service.colorOverlay}
                ${isActive ? 'opacity-95' : 'opacity-80 hover:opacity-90'}
              `} />

              {/* Progress Bar (Only visible on Active) */}
               <div className={`absolute top-8 left-8 right-8 h-[2px] bg-white/30 rounded-full overflow-hidden transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-0'}`}>
                  <div className={`h-full bg-white w-1/3 ${isActive ? 'animate-pulse' : ''}`}></div>
               </div>

              {/* Content Wrapper */}
              <div className="absolute inset-0 p-6 md:p-8 text-white flex flex-col justify-between">
                
                {isActive ? (
                  // --- ACTIVE STATE CONTENT ---
                  <div className="h-full flex flex-col justify-between relative z-10">
                    
                    {/* Top Section: Title & Button */}
                    <div className="flex justify-between items-start mt-8 md:mt-6 animate-fade-in delay-300">
                        <div className="flex flex-col gap-4">
                            {/* Icon shown when active */}
                            <div className="bg-white/10 p-3 rounded-full w-fit backdrop-blur-sm border border-white/20">
                                {service.icon}
                            </div>
                            <h2 className="text-3xl md:text-5xl font-oswald font-medium uppercase tracking-wide leading-tight max-w-2xl">
                                {service.title}
                            </h2>
                        </div>
                        
                        {/* Read More Button (Desktop) */}
                        <button className="hidden md:flex items-center gap-2 px-5 py-2.5 border border-white/40 hover:border-white hover:bg-white/10 rounded-full text-sm font-semibold tracking-wider transition-all duration-300 uppercase"
                           onClick={() => window.location.href = "/core-services"}
                           >
                           Explore <ArrowRight size={16} />
                        </button>
                    </div>

                    {/* Bottom Section: Category & Description */}
                    <div className="max-w-xl animate-slide-up delay-500">
                       <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4 text-white/70 border-l-2 border-white/50 pl-3">
                         {service.category}
                       </p>
                       <p className="text-base md:text-xl font-light leading-relaxed text-white/90 font-inter">
                         {service.description}
                       </p>
                       {/* Mobile Read More Button */}
                       <button className="md:hidden mt-6 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider"
                        onClick={() => window.location.href = "/core-services"}
                        >
                         Explore <ArrowRight size={16} />
                        </button>
                    </div>
                  </div>
                ) : (
                  // --- INACTIVE STATE CONTENT ---
                  <div className="h-full flex flex-col justify-between items-center relative z-10 py-4 transition-opacity duration-500">
                    
                    {/* Top Number/Index */}
                    <span className="text-xl font-oswald font-bold text-white/50">0{service.id}</span>

                    {/* Bottom Vertical Title */}
                    <div className="mt-auto flex flex-col items-center gap-4">
                        {/* Vertical text wrapper */}
                        <div className="hidden md:block writing-vertical-rl rotate-180 transform">
                             <h3 className="text-lg font-bold tracking-widest uppercase whitespace-nowrap text-white/90">
                                {service.title}
                            </h3>
                        </div>
                         {/* Mobile View Title */}
                        <h3 className="md:hidden text-sm font-bold tracking-wider uppercase text-center">
                            {service.title}
                        </h3>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

       {/* CSS for Vertical Text */}
      <style>{`
        .writing-vertical-rl {
            writing-mode: vertical-rl;
        }
      `}</style>
    </section>
  );
};

export default Services;